import { Ref, useCallback, useMemo, useRef, useState } from "react";

import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ObjectHelper } from "@/Common/Helpers/ObjectHelper";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { useDirectoryClientForType } from "@/ContactDirectory/Hooks/useDirectoryClientForType";
import { useDirectoryEntityConstructor } from "@/ContactDirectory/Hooks/useDirectoryEntityConstructor";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models//DirectoryHealthCareProvider";
import { DirectoryOtherEstablishment } from "@/ContactDirectory/Models//DirectoryOtherEstablishment";
import { DirectoryPharmacy } from "@/ContactDirectory/Models//DirectoryPharmacy";
import { DirectoryEntityExistsParameters, DirectoryEntitySearchDuplicateDto } from "../Api/ClientParameters/DirectoryEntityDto";
import { DirectoryEntityMapper } from "../Api/Mapper/DirectoryEntityMapper";
import { useOnPatientStoreUpdateDispatcher } from "../Hooks/useOnPatientStoreUpdateDispatcher";
import { DuplicateSearchType } from "../Models/enums/DuplicateSearchType";
import { IDirectoryEntity } from "../Models/IDirectoryEntity";
import { DirectoryPopupFooter } from "./Common/DirectoryPopupFooter";
import { DirectoryTypeSelectionPopup } from "./Common/DirectoryTypeSelectionPopup";
import { useDuplicateWarningPopupProps } from "./DuplicatePopup/useDuplicateWarningPopupProps";
import { HealthcareProviderTypeForm } from "./HealthcareProvider/HealthcareProviderTypeForm";
import { OtherEstablishmentForm } from "./OtherEstablishment/OtherEstablishmentForm";
import { PharmacyTypeForm } from "./Pharmacy/PharmacyTypeForm";
import { FlagAsInvalidWarningPopup } from "./FlagAsInvalidWarningPopup";
import { IDirectoryFormRawInformation } from "./IDirectoryFormProps";

import styles from "./DirectoryPopupController.module.scss";

interface IDirectoryPopupControllerProps {
    editModel?: IDirectoryEntity;
    model?: IDirectoryEntity;
    defaultType?: DirectoryEntityType;
    directoryTypeSelectionOptions?: DirectoryEntityType[];
    rawInformation?: IDirectoryFormRawInformation;
    onSave?: (entity: IDirectoryEntity) => void;
    onModify?: (entity: IDirectoryEntity) => void;
    onFlagAsInvalid?: (entity: IDirectoryEntity) => void;
    onClose: () => void;
    onEntityUseInstead?: (selection: IDirectoryEntity) => void;
}

function DirectoryPopupController(props: IDirectoryPopupControllerProps): JSX.Element {
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const [selectedType, setDirectoryType] = useState<DirectoryEntityType>(props.defaultType ?? props.editModel?.type ?? DirectoryEntityType.Other);
    const [editModel, setEditModel] = useState<IDirectoryEntity>(props.editModel ?? null);
    const [isFlagAsInvalidPopupVisible, setIsFlagAsInvalidPopupVisible] = useState<boolean>(false);
    const [isEditModelChanged, setIsEditModelChanged] = useState<boolean>(false);
    const [isSearchingForDisallowedDuplicate, setIsSearchingForDisallowedDuplicate] = useState<boolean>(false);

    const updatePatientStore = useOnPatientStoreUpdateDispatcher();

    const { getText } = useTranslation();

    const directoryEntityDataRef = useRef<IDirectoryEntity>(props.model ?? null);
    const saveButtonHandler = useRef<() => Promise<void>>(null);
    const isSearchingAllowedDuplicate = useRef<boolean>(false);

    const title = useMemo<string>(() => editModel ? getText("ModifyDirectoryLabel") : getText("AddToDirectoryLabel"), [editModel, getText]);

    const modelRef: Ref<IDirectoryEntity> = useMemo(() => directoryEntityDataRef, [selectedType]);

    const client = useDirectoryClientForType(selectedType, true);
    const entityConstructor = useDirectoryEntityConstructor(selectedType);

    const { showWarningDuplicateMessage, searchDuplicateForWarning, duplicateWarningPopupProps } = useDuplicateWarningPopupProps(selectedType);

    const searchAllowedDuplicate = useCallback(
        async (currentEntity: IDirectoryEntity, dto: DirectoryEntitySearchDuplicateDto, duplicateSearchType: DuplicateSearchType): Promise<boolean> => {
            isSearchingAllowedDuplicate.current = true;
            const isDuplicate = await searchDuplicateForWarning(currentEntity, dto, duplicateSearchType);
            isSearchingAllowedDuplicate.current = false;

            const save = saveButtonHandler.current;
            saveButtonHandler.current = null;

            if (isDuplicate)
                return true;

            await save?.();
            return false;
        },
        [searchDuplicateForWarning]
    );

    const modifyDuplicateFromWarning = useCallback(
        (model: IDirectoryEntity): void => {
            setEditModel(model);
            duplicateWarningPopupProps.onModify?.(model);
        },
        [duplicateWarningPopupProps]
    );

    const { onModify, onSave, onEntityUseInstead } = props;

    const formProps = useMemo(
        () => ({
            onValidityChange: setIsFormValid,
            checkIfDisallowedDuplicate: async (dto: DirectoryEntityExistsParameters) => {
                setIsSearchingForDisallowedDuplicate(true);
                const exists = await client.exists(dto);
                setIsSearchingForDisallowedDuplicate(false);
                return exists;
            },
            onEntityUseInstead: (entity: IDirectoryEntity) => onEntityUseInstead(entity),
            showWarningDuplicateMessage,
            searchDuplicateForWarning: searchAllowedDuplicate,
            duplicateWarningPopupProps: { ...duplicateWarningPopupProps, onModify: (entity: IDirectoryEntity) => modifyDuplicateFromWarning(entity) }
        }),
        [client, duplicateWarningPopupProps, showWarningDuplicateMessage, onEntityUseInstead, searchAllowedDuplicate, modifyDuplicateFromWarning]
    );

    const modifyEntity = useCallback(
        async (): Promise<void> => {
            try {
                const model = DirectoryEntityMapper.toModifyDto(modelRef.current);
                const modifiedModelDto = await client.modify(model);
                const modifiedModel = entityConstructor(modifiedModelDto);

                onModify(modifiedModel);
                updatePatientStore(modifiedModel, selectedType);
            }
            catch (e) {
                console.error(e);
            }
        },
        [modelRef, selectedType, client, entityConstructor, updatePatientStore, onModify]
    );

    const addEntity = useCallback(
        async (): Promise<void> => {
            try {
                const model = DirectoryEntityMapper.toAddDto(modelRef.current);
                const modifiedModel = await client.add(model);
                onSave(entityConstructor(modifiedModel));
            }
            catch (e) {
                console.error(e);
            }
        },
        [modelRef, client, entityConstructor, onSave]
    );

    const updateModel = useCallback(
        (updatedModel: IDirectoryEntity) => {
            const isModelChanged = !ObjectHelper.equals(updatedModel, editModel, true);
            setIsEditModelChanged(isModelChanged);
            directoryEntityDataRef.current = updatedModel;
        },
        [directoryEntityDataRef, editModel]
    );

    return (
        <Modal
            title={title}
            onClose={() => props.onClose()}
            disableOverlayClose
            showX
            showOverlay
            draggable
            fixedOverlayPosition
            centerContent
            top={10}
            footer={editModel ? (
                <DirectoryPopupFooter
                    isValid={isFormValid && isEditModelChanged && !isSearchingForDisallowedDuplicate}
                    onCancel={() => props.onClose()}
                    onSave={async () => {
                        if (isSearchingAllowedDuplicate.current)
                            saveButtonHandler.current = modifyEntity;
                        else
                            await modifyEntity();
                    }}
                    onInvalidate={() => setIsFlagAsInvalidPopupVisible(true)}
                />
            ) : (
                <DirectoryPopupFooter
                    isValid={isFormValid && !isSearchingForDisallowedDuplicate}
                    onCancel={() => props.onClose()}
                    onSave={async () => {
                        if (isSearchingAllowedDuplicate.current)
                            saveButtonHandler.current = addEntity;
                        else
                            await addEntity();
                    }}
                />
            )
            }
        >
            {(editModel && isFlagAsInvalidPopupVisible) && (
                <FlagAsInvalidWarningPopup
                    entityId={modelRef.current.id}
                    closePopup={() => setIsFlagAsInvalidPopupVisible(false)}
                    flagAsInvalid={async (id, reason) => {
                        await client.flagAsInvalid(id, reason);
                        setIsFlagAsInvalidPopupVisible(false);
                        props.onFlagAsInvalid?.(modelRef.current);
                        props.onClose();
                    }}
                />
            )}
            <div>
                {(!editModel && IsNullOrUndefined(props.defaultType)) && (
                    <DirectoryTypeSelectionPopup
                        type={selectedType}
                        onChange={type => setDirectoryType(type)}
                        options={props.directoryTypeSelectionOptions}
                    />
                )}
                <div>
                    <div className={styles.selectedForm}>
                        {selectedType === DirectoryEntityType.HealthcareProvider && (
                            <HealthcareProviderTypeForm
                                {...formProps}
                                editModel={editModel ? directoryEntityDataRef.current as DirectoryHealthCareProvider : null}
                                onModelUpdate={model => updateModel(model)}
                            />
                        )}
                        {selectedType === DirectoryEntityType.Pharmacy && (
                            <PharmacyTypeForm
                                {...formProps}
                                editModel={editModel ? directoryEntityDataRef.current as DirectoryPharmacy : null}
                                onModelUpdate={model => updateModel(model)}
                            />
                        )}
                        {selectedType === DirectoryEntityType.Other && (
                            <OtherEstablishmentForm
                                {...formProps}
                                rawInformation={props.rawInformation}
                                editModel={editModel ? directoryEntityDataRef.current as DirectoryOtherEstablishment : null}
                                onModelUpdate={model => updateModel(model)}
                            />
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export { DirectoryPopupController, IDirectoryPopupControllerProps };
